<template>
  <main class="landing-creator">
    <div v-if="landingPage && !editMode" class="editor-container">
      <section class="edit-section mb-3">
        <h5 class="landing-url">
          <i class="mdi mdi-lock"/>
          <a :href="landingURL" target="_blank">{{landingURL}}</a>
        </h5> 
        <button class="btn btn-rounded btn-info float-right" @click="goToEditMode" v-if="canEdit">Edit Page</button>
      </section>
      <iframe class="virtual-landing" :srcdoc="landingSource" />
    </div>
    <div v-else-if="editMode" class="editor-container">
      <section class="editor-section">
        <code-editor
          language="htmlmixed"
          lineNumbers
          @input="editCode"
          :value="landingCode"/>
      </section>
      <section class="text-right mt-3">
        <button class="btn btn-cancel btn-edit btn-inverse" @click="closeEditing">Cancel</button>
        <button class="btn btn-save btn-edit btn-info ml-2" @click="updateLandingPage">Save Changes</button>
      </section>
    </div>
    <div v-else class="virtual-landing">
      <section class="virtual-header">
        <div class="logo"/>
        <div class="nav">
          <div class="nav-link"/>
          <div class="nav-link"/>
          <div class="nav-link"/>
          <div class="nav-link"/>
        </div>
      </section>
      <section class="virtual-primary-content">
        <div class="mb-3" v-if="isUserAdmin" style="min-width: 400px">
          <input
            type="url"
            v-model="themeUrl"
            placeholder="Enter theme URL (optional)"
            class="form-control"
          />
        </div>
        <div>
          <button @click="generateLandingPage" class="btn btn-info generate-landing">
            Generate Landing Page
          </button>
        </div>
      </section>
      <section class="virtual-secondary-content">
        <div class="primary-block"/>
        <div class="light-block"/>
        <div class="secondary-block"/>
      </section>
    </div>
  </main>
</template>
<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import pick from 'lodash/pick';
import CodeEditor from '@/components/CodeEditor';
import LANDING_PAGE from '@/graphql/Campaigns/LandingPage.gql';
import EDIT_LANDING_PAGE from '@/graphql/Campaigns/EditLandingPage.gql';
import CREATE_LANDING_PAGE from '@/graphql/Campaigns/CreateLandingPage.gql';
// import { getWidgetCode } from '@/helpers/widget';

export default {
  name: 'LandingPage',
  components: { CodeEditor },
  props: { campaignId: Number },
  computed: {
    ...mapGetters(['campaign', 'isUserAdmin', 'isWhiteLabel']),
    landingURL() {
      return (this.campaign.whiteLabel) ? `${process.env.PAGES_WHITELABEL_URI}/${this.landingPage.name}` : `${process.env.PAGES_URI}/${this.landingPage.name}`;
    },
    landingCode() {
      return [
        {
          title: 'body.html',
          code: this.draft.body,
        },
        {
          title: 'head.html',
          code: this.draft.head,
        },
      ];
    },
    landingSource() {
      return `
        <html>
          <head>${this.landingPage.head}</head>
          <body>
            ${this.landingPage.body}
          </body>
        </html>
      `;
    },
    canEdit() {
      if (this.isUserAdmin) {
        return true;
      }
      return (this.campaign) ? this.campaign.editable: false;
    },
  },
  methods: {
    async generateLandingPage() {
      const variables = pick(this, ['campaignId']);
      if (this.themeUrl) {
        variables.input = {url: this.themeUrl};
      }
      this.landingPage = (await this.$apollo.mutate({
        mutation: CREATE_LANDING_PAGE,
        variables,
      })).data.createLanding;
      this.$emit('created', true);
    },
    async updateLandingPage() {
      this.editMode = false;
      Object.assign(this.landingPage, this.draft);
      await this.$apollo.mutate({
        mutation: EDIT_LANDING_PAGE,
        variables: {
          campaignId: this.campaignId,
          input: pick(this.draft, ['body', 'head', 'settings']),
        },
      });
    },
    closeEditing() {
      this.editMode = false;
      Object.assign(this.draft, this.landingPage);
    },
    goToEditMode() {
      this.editMode = true;
    },
    editCode([body, head]) {
      this.draft.body = body.code;
      this.draft.head = head.code;
    },
  },
  data() {
    return {
      landingPage: null,
      draft: '',
      editMode: false,
      themeUrl: null,
    };
  },
  apollo: {
    landingPage: {
      query: LANDING_PAGE,
      loadingKey: 'showSpinner',
      fetchPolicy: 'network-only',
      variables() {
        return {
          campaignId: this.campaignId,
        };
      },
      result({ data, loading }) {
        if (loading) {
          return;
        }
        if (data.landingPage) {
          Vue.set(this, 'landingPage', { ...data.landingPage });
          Vue.set(this, 'draft', { ...data.landingPage });
          this.$emit('created', Boolean(data.landingPage));
        }
      },
    },
  },
};
</script>
<style scoped>
.generate-landing {
  font-size: 1.5em;
}
.editor-container {
  display: flex;
  flex: 1;
  max-width: 100%;
  position: relative;
  flex-direction: column;
}
.edit-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.landing-url {
  margin: 0;
  flex: 0.98;
  background-color: #f0f3f4;
  height: 2.2rem;
  padding-left: 5px;
  border-radius: 1rem;
  display: flex;
  align-items: center;
}
.landing-url .mdi-lock {
  color: #5e6368;
  margin: 0 7px;
}
.landing-creator {
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
}
.virtual-landing {
  position: relative;
  height: 620px;
  width: 100%;
  border: 0;
}
.editor-section {
  width: 100%;
  height: 578px;
}
.virtual-landing .virtual-header {
  height: 55px;
  background-color: #e9edee;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 7%;
}
.virtual-landing .virtual-header .logo {
  position: relative;
  width: 40px;
  height: 40px;
  background-color: #fff;
  border-radius: 50%;
}

.virtual-landing .virtual-header .nav {
  display: flex;
  width: 70%;
  align-items: center;
  justify-content: space-between;
}

.virtual-landing .virtual-header .nav-link {
  position: relative;
  width: 17%;
  height: 12px;
  padding: 0;
  background-color: #fff;
}

.virtual-landing .virtual-primary-content {
  display: flex;
  flex-direction: column;
  border: 5px solid #e9edee;
  border-top: 0;
  border-bottom: 0;
  justify-content: center;
  align-items: center;
  height: 60%;
}

.virtual-landing .virtual-secondary-content {
  display: flex;
  height: 30%;
  border-bottom: 5px solid #e9edee;
}

.virtual-landing .virtual-secondary-content div {
  position: relative;
  width: 33.33%;
}

.virtual-landing .virtual-secondary-content .primary-block {
  background-color: #dfe4e5;
}

.virtual-landing .virtual-secondary-content .secondary-block {
  background-color: #e9edee;
}

.virtual-landing .virtual-secondary-content .light-block {
  background-color: #eef4f5;
}
</style>



// WEBPACK FOOTER //
// src/pages/Campaigns/Details/LandingPage.vue